import {
  ViewPlugin,
  type ViewUpdate,
  type DecorationSet,
  Decoration,
  EditorView,
} from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";
import { syntaxTree, ensureSyntaxTree } from "@codemirror/language";
import { buildHeadingDecorations } from "./headings";
import { buildEmphasisDecorations } from "./emphasis";
import { buildCodeDecorations } from "./code";
import { buildListDecorations } from "./lists";
import { buildBlockquoteDecorations } from "./blockquote";
import { buildLinkDecorations } from "./links";
import { buildHorizontalRuleDecorations } from "./horizontal-rule";
import { buildWikilinkDecorations } from "../wikilinks";
import { buildContextRefDecorations } from "../context-references";
import { buildVariableDecorations } from "../variables";
import { activeCursor } from "./cursor";

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

class LivePreviewPlugin {
  decorations: DecorationSet;

  _treeVersion: number = 0;
  _hadFocus: boolean = false;

  constructor(view: EditorView) {
    // Force synchronous parse so decorations are correct on first render
    ensureSyntaxTree(view.state, view.state.doc.length, 500);
    this.decorations = this.buildDecorations(view);
    this._treeVersion = syntaxTree(view.state).length;
  }

  update(update: ViewUpdate) {
    const newTreeVersion = syntaxTree(update.state).length;
    const focusChanged = update.view.hasFocus !== this._hadFocus;
    this._hadFocus = update.view.hasFocus;
    if (
      update.docChanged ||
      update.selectionSet ||
      update.viewportChanged ||
      focusChanged ||
      newTreeVersion !== this._treeVersion
    ) {
      this._treeVersion = newTreeVersion;
      this.decorations = this.buildDecorations(update.view);
    }
  }

  buildDecorations(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    const state = view.state;
    const tree = syntaxTree(state);

    // The cursor context (positions + precomputed active line numbers). When
    // the editor is NOT focused, activeCursor() returns all -1 — so no line and
    // no range matches any node and ALL markdown renders styled (preview mode).
    // See cursor.ts for why this is -1 and not a position like doc.length.
    const cursor = activeCursor(view);

    // Collect decorations from each module
    const decorations: DecorationEntry[] = [];

    // Context refs are collected first so we can tell the link builder
    // to skip Link nodes that overlap with @[label](ref:...) ranges.
    // Without this, the markdown parser treats [label](ref:...) as a
    // regular link and creates conflicting decorations.
    const contextRefRanges: Array<{ from: number; to: number }> = [];

    for (const range of view.visibleRanges) {
      buildContextRefDecorations(state, range, cursor, decorations, contextRefRanges);
    }

    for (const range of view.visibleRanges) {
      buildHeadingDecorations(state, tree, range, cursor, decorations);
      buildEmphasisDecorations(state, tree, range, cursor, decorations);
      buildCodeDecorations(state, tree, range, cursor, decorations);
      buildListDecorations(state, tree, range, cursor, decorations);
      buildBlockquoteDecorations(state, tree, range, cursor, decorations);
      buildLinkDecorations(state, tree, range, cursor, decorations, contextRefRanges);
      buildHorizontalRuleDecorations(state, tree, range, cursor, decorations);
      buildWikilinkDecorations(state, range, cursor, decorations);
      buildVariableDecorations(state, range, cursor, decorations);
    }

    // Sort by position (required by RangeSetBuilder)
    decorations.sort((a, b) => a.from - b.from || a.to - b.to);

    for (const d of decorations) {
      builder.add(d.from, d.to, d.decoration);
    }

    return builder.finish();
  }
}

const livePreviewViewPlugin = ViewPlugin.fromClass(LivePreviewPlugin, {
  decorations: (v) => v.decorations,
});

/**
 * Ctrl/Cmd+click on a rendered markdown link opens the URL in a new tab.
 */
const markdownLinkClickHandler = EditorView.domEventHandlers({
  mousedown(event: MouseEvent, view: EditorView) {
    if (!(event.ctrlKey || event.metaKey)) return false;

    const target = event.target as HTMLElement;
    if (!target.closest(".cm-md-link")) return false;

    const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
    if (pos === null) return false;

    // Walk the syntax tree to find the Link node and extract the URL
    const tree = syntaxTree(view.state);
    let url: string | null = null;

    tree.iterate({
      from: pos,
      to: pos,
      enter(node) {
        if (node.name === "Link") {
          // Find URL child
          const childCursor = node.node.cursor();
          if (childCursor.firstChild()) {
            do {
              if (childCursor.name === "URL") {
                url = view.state.doc.sliceString(childCursor.from, childCursor.to);
              }
            } while (childCursor.nextSibling());
          }
          return false;
        }
      },
    });

    // `url` is only assigned inside the iterate() callback above, which TS's
    // control-flow analysis doesn't track — it still sees the initial `null`, so
    // a fresh annotated binding is needed to recover the real string | null type.
    const linkUrl: string | null = url;
    if (linkUrl) {
      event.preventDefault();
      // Ensure URL has a protocol, otherwise browser treats it as relative
      let href: string = linkUrl;
      if (!/^https?:\/\//i.test(href) && !/^mailto:/i.test(href)) {
        href = "https://" + href;
      }
      window.open(href, "_blank", "noopener");
      return true;
    }

    return false;
  },
});

/**
 * Live preview plugin + markdown link click handler bundled together.
 */
export const livePreviewPlugin = [livePreviewViewPlugin, markdownLinkClickHandler];
